import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * A mock case row whose status badge walks the default workflow:
 * New → Open → Pending → Waiting on Client → Closed. Uses the real
 * .badge.status-* classes (status-new/open/inv/wait/res in global.css), so
 * the soft blue→teal ramp shown here is exactly what the app renders.
 * A dimmed ramp underneath keeps every stage visible while the active one
 * is highlighted. Status names are the app defaults — admin-configurable.
 */

const FLOW = [
  { key: 'new',    cls: 'status-new',  label: 'New' },
  { key: 'open',   cls: 'status-open', label: 'Open' },
  { key: 'pending', cls: 'status-inv',  label: 'Pending' },
  { key: 'wait',   cls: 'status-wait', label: 'Waiting on Client' },
  { key: 'closed', cls: 'status-res',  label: 'Closed' },
];

const STEPS = [
  {
    caption: 'New — every submission starts here automatically; nobody has to set it.',
    scene: 'new', ms: 2400,
    cursor: 'stage-new',
  },
  {
    caption: 'Open — the team working the case changes status from the case header. Clinic Manager accounts see the status read-only.',
    scene: 'open', ms: 2800,
    cursor: 'stage-open', click: true,
  },
  {
    caption: 'Pending — under active investigation. Status names come from your admin’s configuration, so yours may differ.',
    scene: 'pending', ms: 2600,
    cursor: 'stage-pending', click: true,
  },
  {
    caption: 'Waiting on Client — paused until the client responds; the badge cools toward teal as the case nears resolution.',
    scene: 'wait', ms: 2600,
    cursor: 'stage-wait', click: true,
  },
  {
    caption: 'Closed — set by Mark resolved with a short resolution note. Admin roles can reopen a closed case.',
    scene: 'closed', ms: 3000,
    cursor: 'stage-closed', click: true,
  },
];

export default function StatusFlowDemo() {
  return (
    <DemoStage steps={STEPS} label="How a case moves through statuses" height={190}>
      {(step) => {
        const scene = step?.scene ?? 'new';
        const active = FLOW.find(s => s.key === scene) ?? FLOW[0];
        return (
          <div className="kb-mock">
            {/* Mock case row — the badge swaps as the case progresses */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 16px', marginBottom: 18,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 10,
            }}>
              <span className="case-id" style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12, color: 'var(--text-2)',
              }}>AE-042</span>
              <span style={{ fontSize: 13, color: 'var(--text-2)', flex: 1 }}>Clinic_A</span>
              {/* keyed so the pop animation retriggers on each change */}
              <span key={active.key} className={`badge ${active.cls} kb-pop`}>
                <span className="dot" />{active.label}
              </span>
            </div>

            {/* The full ramp — active stage highlighted, the rest dimmed */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              {FLOW.map((s, i) => (
                <React.Fragment key={s.key}>
                  {i > 0 && <span style={{ color: 'var(--text-3)', fontSize: 11 }}>→</span>}
                  <span
                    data-kb-target={`stage-${s.key}`}
                    className={`badge ${s.cls}`}
                    style={{
                      opacity: s.key === active.key ? 1 : 0.35,
                      transform: s.key === active.key ? 'scale(1.06)' : 'scale(1)',
                      transition: 'opacity 300ms var(--ease), transform 300ms var(--ease)',
                    }}
                  >
                    <span className="dot" />{s.label}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
